/**
 * Transient notifications.
 *
 * The context lives apart from the provider so that editing the provider does
 * not blow away React Fast Refresh state — the same split as the theme and
 * access packages.
 */

import { createContext, useContext } from "react";

export type ToastTone = "info" | "success" | "warning" | "danger";

export interface Toast {
  id: string;
  tone: ToastTone;
  message: string;
  /** Milliseconds before it dismisses itself. */
  durationMs: number;
}

export interface ToastOptions {
  tone?: ToastTone;
  durationMs?: number;
}

export interface ToastContextValue {
  toasts: readonly Toast[];
  show: (message: string, options?: ToastOptions) => string;
  dismiss: (id: string) => void;
}

export const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * How long a toast stays.
 *
 * Long enough to read: the usual 3 seconds is below the time it takes many
 * people to notice a message, move their eyes to it and read it — and a
 * notification nobody managed to read is the same as no notification.
 */
export const DEFAULT_TOAST_MS = 6000;

/** Errors do not dismiss themselves. */
export const PERSISTENT = 0;

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) {
    throw new Error("useToast must be used inside <Toaster>.");
  }
  return value;
}
